import type {
  CaptureMessages,
  CaptureTheme,
  FingerType,
  GuidanceCode,
} from "./types";
import {
  captureGuideFor,
  captureHandFor,
  captureModeFor,
  captureTargetName,
} from "./target";
import type { InternalFrameAnalysis } from "./quality";
import type { InternalQualityPolicy } from "./policy";

const DEFAULT_MESSAGES: Required<CaptureMessages> = {
  title: "Capture fingerprint",
  subtitle: "Use the rear camera and place one finger inside the guide.",
  cancel: "Cancel",
  startingCamera: "Starting camera…",
  placeFinger: "Place your finger inside the guide",
  moveCloser: "Move your finger closer",
  moveBack: "Move your finger slightly back",
  centerFinger: "Center your finger",
  holdStill: "Hold still",
  tooDark: "More light is needed",
  tooBright: "The image is too bright",
  reduceGlare: "Tilt slightly to reduce glare",
  imageBlurry: "Hold steady and let the camera focus",
  ridgesNotVisible: "Adjust distance until ridges are clear",
  ready: "Perfect — hold still",
  capturing: "Capturing the best frame…",
  enhancing: "Enhancing ridge detail…",
};

function fourFingerMessages(
  finger: FingerType,
): Partial<Required<CaptureMessages>> {
  const hand = captureHandFor(finger);
  if (!hand) return {};

  return {
    title: `Capture ${hand} hand`,
    subtitle: `Place the four fingers of your ${hand} hand inside the guide. Keep your thumb outside.`,
    placeFinger: "Place all four fingers inside the guide",
    moveCloser: "Move your hand closer",
    moveBack: "Move your hand slightly back",
    centerFinger: "Center all four fingers",
  };
}

function individualFingerMessages(
  finger: FingerType,
): Partial<Required<CaptureMessages>> {
  const target = captureTargetName(finger);
  if (!target || target.endsWith("four fingers")) return {};
  const isThumb = target.endsWith("thumb");

  return {
    title: `Capture ${target}`,
    subtitle: `Place the pad of your ${target}${isThumb ? "" : " finger"} inside the guide.`,
  };
}

const GUIDANCE_MESSAGE: Record<
  GuidanceCode,
  keyof Required<CaptureMessages>
> = {
  STARTING_CAMERA: "startingCamera",
  PLACE_FINGER: "placeFinger",
  MOVE_CLOSER: "moveCloser",
  MOVE_BACK: "moveBack",
  CENTER_FINGER: "centerFinger",
  HOLD_STILL: "holdStill",
  TOO_DARK: "tooDark",
  TOO_BRIGHT: "tooBright",
  REDUCE_GLARE: "reduceGlare",
  IMAGE_BLURRY: "imageBlurry",
  RIDGES_NOT_VISIBLE: "ridgesNotVisible",
  READY: "ready",
  CAPTURING: "capturing",
  ENHANCING: "enhancing",
};

export class CaptureModal {
  readonly video: HTMLVideoElement;
  private readonly host: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly guidance: HTMLParagraphElement;
  private readonly guide: HTMLDivElement;
  private readonly messages: Required<CaptureMessages>;
  private readonly debugPanel: HTMLDivElement;
  private lastAnalysis?: InternalFrameAnalysis;
  private lastPolicy?: Readonly<InternalQualityPolicy>;
  private lastFocusStrategy = "browser auto";

  constructor(
    onCancel: () => void,
    theme: CaptureTheme = {},
    messages: CaptureMessages = {},
    finger: FingerType = "unspecified",
    debug = false,
  ) {
    const captureMode = captureModeFor(finger);
    this.messages = {
      ...DEFAULT_MESSAGES,
      ...fourFingerMessages(finger),
      ...individualFingerMessages(finger),
      ...messages,
    };
    this.host = document.createElement("div");
    this.host.dataset.touchlessFingerprint = "modal";
    const shadow = this.host.attachShadow({ mode: "open" });
    const accent = theme.accentColor ?? "#42e8a1";
    const background = theme.backgroundColor ?? "#07120f";
    const text = theme.textColor ?? "#f4fff9";
    const muted = theme.mutedColor ?? "#a5bbb2";

    shadow.innerHTML = `
      <style>
        :host { color-scheme: dark; }
        * { box-sizing: border-box; }
        .shell {
          --tf-accent: ${accent};
          --tf-bg: ${background};
          --tf-text: ${text};
          --tf-muted: ${muted};
          position: fixed;
          inset: 0;
          z-index: 2147483647;
          display: grid;
          grid-template-rows: auto minmax(0, 1fr) auto;
          overflow: hidden;
          background:
            radial-gradient(circle at 50% 25%, color-mix(in srgb, var(--tf-accent) 10%, transparent), transparent 34%),
            var(--tf-bg);
          color: var(--tf-text);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .topbar {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: max(18px, env(safe-area-inset-top)) 20px 14px;
        }
        .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 7px;
          color: var(--tf-accent);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .13em;
          text-transform: uppercase;
        }
        .eyebrow::before {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--tf-accent);
          box-shadow: 0 0 12px var(--tf-accent);
          content: "";
        }
        h1 {
          margin: 0;
          font-size: clamp(21px, 5.5vw, 28px);
          letter-spacing: -.035em;
          line-height: 1.1;
        }
        .cancel {
          flex: none;
          border: 1px solid rgba(255,255,255,.14);
          border-radius: 999px;
          padding: 10px 14px;
          background: rgba(255,255,255,.07);
          color: var(--tf-text);
          font: inherit;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }
        .camera-area {
          min-height: 0;
          display: grid;
          place-items: center;
          padding: 0 14px;
        }
        .stage {
          position: relative;
          width: min(100%, 620px);
          max-height: 100%;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 28px;
          background: #020604;
          box-shadow: 0 24px 80px rgba(0,0,0,.38);
        }
        video {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
          transform: translateZ(0);
        }
        .guide {
          --progress: 0deg;
          position: absolute;
          top: var(--guide-top);
          left: var(--guide-left);
          width: var(--guide-width);
          height: var(--guide-height);
          border: 2px solid rgba(255,255,255,.72);
          border-radius: 48% 48% 42% 42% / 28% 28% 20% 20%;
          box-shadow:
            0 0 0 999px rgba(0,0,0,.48),
            inset 0 0 28px rgba(66,232,161,.08);
          transition: border-color .2s ease, filter .2s ease;
          pointer-events: none;
        }
        .guide::before {
          position: absolute;
          inset: -7px;
          border-radius: inherit;
          padding: 3px;
          background: conic-gradient(var(--tf-accent) var(--progress), transparent 0);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          content: "";
        }
        .guide[data-ready="true"] {
          border-color: var(--tf-accent);
          filter: drop-shadow(0 0 12px color-mix(in srgb, var(--tf-accent) 45%, transparent));
        }
        .guide[data-mode="four-fingers"] {
          border-radius: 24% 24% 18% 18% / 28% 28% 18% 18%;
        }
        .guide[data-mode="four-fingers"] .ridge-lines {
          inset: 16% 6%;
          border-radius: 22%;
          background:
            repeating-linear-gradient(90deg, transparent 0 8px, color-mix(in srgb, var(--tf-accent) 48%, transparent) 9px 10px, transparent 11px 15px);
          mask-image: linear-gradient(to bottom, transparent, black 18%, black 78%, transparent);
        }
        .ridge-lines {
          position: absolute;
          inset: 23% 17%;
          opacity: .32;
          background:
            repeating-radial-gradient(ellipse at 50% 20%, transparent 0 7px, var(--tf-accent) 8px 9px, transparent 10px 14px);
          border-radius: 50%;
          mask-image: linear-gradient(to bottom, transparent, black 20%, black 75%, transparent);
        }
        .footer {
          padding: 16px 20px max(22px, env(safe-area-inset-bottom));
          text-align: center;
        }
        .guidance {
          min-height: 25px;
          margin: 0 0 7px;
          font-size: 17px;
          font-weight: 750;
          letter-spacing: -.015em;
        }
        .debug-panel {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 5px 8px;
          width: min(100%, 520px);
          margin: 12px auto 0;
          padding: 10px;
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 12px;
          background: rgba(0,0,0,.42);
          font: 600 10px/1.25 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          text-align: left;
        }
        .debug-panel[hidden] { display: none; }
        .debug-title {
          grid-column: 1 / -1;
          color: var(--tf-muted);
          letter-spacing: .08em;
          text-transform: uppercase;
        }
        .debug-check {
          display: flex;
          justify-content: space-between;
          gap: 6px;
          color: #ffadad;
        }
        .debug-check[data-pass="true"] { color: var(--tf-accent); }
        .debug-check[data-pending="true"] { color: var(--tf-muted); }
        .debug-fingers { grid-column: 1 / -1; }
        .subtitle {
          max-width: 520px;
          margin: 0 auto;
          color: var(--tf-muted);
          font-size: 13px;
          line-height: 1.5;
        }
        @media (min-width: 720px) {
          .topbar { padding-inline: 32px; }
          .footer { padding-bottom: 28px; }
          .stage { max-height: calc(100vh - 210px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .guide { transition: none; }
        }
      </style>
      <section class="shell" role="dialog" aria-modal="true" aria-labelledby="tf-title">
        <header class="topbar">
          <div>
            <div class="eyebrow">Captured on this device</div>
            <h1 id="tf-title"></h1>
          </div>
          <button class="cancel" type="button"></button>
        </header>
        <main class="camera-area">
          <div class="stage">
            <video muted playsinline aria-label="Camera preview"></video>
            <div class="guide" aria-hidden="true">
              <div class="ridge-lines"></div>
            </div>
          </div>
        </main>
        <footer class="footer" aria-live="polite">
          <p class="guidance"></p>
          <p class="subtitle"></p>
          <div class="debug-panel" hidden aria-label="Live quality diagnostics"></div>
        </footer>
      </section>
    `;

    this.video = shadow.querySelector("video") as HTMLVideoElement;
    this.stage = shadow.querySelector(".stage") as HTMLDivElement;
    this.guidance = shadow.querySelector(
      ".guidance",
    ) as HTMLParagraphElement;
    this.guide = shadow.querySelector(".guide") as HTMLDivElement;
    this.guide.dataset.mode = captureMode;
    const guide = captureGuideFor(captureMode);
    this.guide.style.setProperty("--guide-top", `${guide.top * 100}%`);
    this.guide.style.setProperty("--guide-left", `${guide.left * 100}%`);
    this.guide.style.setProperty("--guide-width", `${guide.width * 100}%`);
    this.guide.style.setProperty("--guide-height", `${guide.height * 100}%`);
    this.debugPanel = shadow.querySelector(".debug-panel") as HTMLDivElement;
    this.debugPanel.hidden = !debug;
    const cancel = shadow.querySelector(".cancel") as HTMLButtonElement;
    const title = shadow.querySelector("h1") as HTMLHeadingElement;
    const subtitle = shadow.querySelector(".subtitle") as HTMLParagraphElement;

    title.textContent = this.messages.title;
    subtitle.textContent = this.messages.subtitle;
    cancel.textContent = this.messages.cancel;
    cancel.addEventListener("click", onCancel, { once: true });
    document.body.append(this.host);
    this.update("STARTING_CAMERA", 0);
  }

  setAspectRatio(width: number, height: number): void {
    this.stage.style.aspectRatio = `${width} / ${height}`;
  }

  update(
    code: GuidanceCode,
    stableProgress: number,
    analysis?: InternalFrameAnalysis,
    policy?: Readonly<InternalQualityPolicy>,
    focusStrategy?: string,
    phase:
      | "placement"
      | "focusing"
      | "detail"
      | "capturing"
      | "enhancing" = "placement",
  ): void {
    this.guidance.textContent = this.messages[GUIDANCE_MESSAGE[code]];
    this.guide.style.setProperty(
      "--progress",
      `${Math.round(Math.max(0, Math.min(1, stableProgress)) * 360)}deg`,
    );
    this.guide.dataset.ready = String(
      code === "READY" || code === "CAPTURING" || code === "ENHANCING",
    );
    if (analysis && policy) {
      this.lastAnalysis = analysis;
      this.lastPolicy = policy;
    }
    if (focusStrategy) this.lastFocusStrategy = focusStrategy;
    if (
      this.lastAnalysis &&
      this.lastPolicy &&
      !this.debugPanel.hidden
    ) {
      this.updateDiagnostics(
        this.lastAnalysis,
        this.lastPolicy,
        code,
        this.lastFocusStrategy,
        phase,
      );
    }
  }

  private updateDiagnostics(
    analysis: InternalFrameAnalysis,
    policy: Readonly<InternalQualityPolicy>,
    code: GuidanceCode,
    focusStrategy = "browser auto",
    phase:
      | "placement"
      | "focusing"
      | "detail"
      | "capturing"
      | "enhancing" = "placement",
  ): void {
    const { metrics } = analysis;
    const detailPending = phase === "placement" || phase === "focusing";
    const percent = (value: number): string => Math.round(value * 100) + "%";
    const checks = [
      {
        name: "coverage",
        value: percent(metrics.coverage),
        limit: percent(policy.minimumCoverage) + "-" + percent(policy.maximumCoverage),
        pass:
          metrics.coverage >= policy.minimumCoverage &&
          metrics.coverage <= policy.maximumCoverage,
      },
      {
        name: "center",
        value: percent(metrics.centerOffset),
        limit: "<=" + percent(policy.maximumCenterOffset),
        pass: metrics.centerOffset <= policy.maximumCenterOffset,
      },
      {
        name: "sharpness",
        value: detailPending ? "pending" : percent(metrics.sharpness),
        limit: ">=" + percent(policy.minimumSharpness),
        pass: detailPending || metrics.sharpness >= policy.minimumSharpness,
        pending: detailPending,
      },
      {
        name: "ridges",
        value: detailPending ? "pending" : percent(metrics.ridgeClarity),
        limit: ">=" + percent(policy.minimumRidgeClarity),
        pass: detailPending || metrics.ridgeClarity >= policy.minimumRidgeClarity,
        pending: detailPending,
      },
      {
        name: "contrast",
        value: percent(metrics.contrast),
        limit: ">=" + percent(policy.minimumContrast),
        pass: metrics.contrast >= policy.minimumContrast,
      },
      {
        name: "exposure",
        value: percent(metrics.exposure),
        limit: ">=" + percent(policy.minimumExposure),
        pass: metrics.exposure >= policy.minimumExposure,
      },
      {
        name: "stability",
        value: percent(1 - metrics.motion),
        limit: ">=" + percent(1 - policy.maximumMotion),
        pass: metrics.motion <= policy.maximumMotion,
      },
      {
        name: "glare",
        value: percent(metrics.glare),
        limit: "<=" + percent(policy.maximumGlare),
        pass: metrics.glare <= policy.maximumGlare,
      },
    ];

    this.debugPanel.replaceChildren();
    const title = document.createElement("div");
    title.className = "debug-title";
    title.textContent =
      "Live checks - " +
      code +
      " | phase: " +
      phase +
      " | focus: " +
      focusStrategy;
    this.debugPanel.append(title);
    for (const check of checks) {
      const row = document.createElement("div");
      row.className = "debug-check";
      row.dataset.pass = String(check.pass);
      row.dataset.pending = String(Boolean("pending" in check && check.pending));
      const label = document.createElement("span");
      label.textContent = check.name;
      const value = document.createElement("span");
      value.textContent = check.value + " / " + check.limit;
      row.append(label, value);
      this.debugPanel.append(row);
    }

    if (metrics.fingerSharpness.length === 4) {
      const row = document.createElement("div");
      row.className = "debug-check debug-fingers";
      row.dataset.pass = String(
        metrics.sharpness >= policy.minimumSharpness,
      );
      const label = document.createElement("span");
      label.textContent = "finger detail";
      const value = document.createElement("span");
      value.textContent = metrics.fingerSharpness.map(percent).join(" / ");
      row.append(label, value);
      this.debugPanel.append(row);
    }
  }

  destroy(): void {
    this.host.remove();
  }
}
